import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useI18n } from './i18n';

export function Spinner({ dark = false }: { dark?: boolean }) {
  return <span className={dark ? 'spinner dark' : 'spinner'} aria-hidden="true" />;
}

export function Loading({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="empty" role="status" aria-live="polite">
      <div className="row" style={{ justifyContent: 'center' }}>
        <Spinner dark />
        <span>{label ?? t.common.loading}</span>
      </div>
    </div>
  );
}

export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack" style={{ padding: 18 }} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 15, width: `${92 - i * 9}%` }} />
      ))}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {hint ? <div>{hint}</div> : null}
      {action ? <div style={{ marginTop: 14 }}>{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="empty" role="alert">
      <div className="empty-title">{t.common.error}</div>
      <div>{message}</div>
      {onRetry ? (
        <div style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-ghost" onClick={onRetry}>
            {t.common.retry}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Modal dialog with the accessibility basics that are easy to skip and painful
 * to retrofit: focus moves in on open and returns on close, Escape closes,
 * Tab is trapped, and the backdrop is not the only way out (spec §2).
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  // The latest onClose, without re-running the effect: a caller that passes a
  // new function on every render (an inline arrow) must not move the focus
  // back to the first field on every keystroke.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [
        ...ref.current.querySelectorAll<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      restoreTo.current?.focus?.();
    };
  }, []);

  return (
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            {t.common.close}
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>
        {label}
        {required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
        {hint ? <span className="hint"> — {hint}</span> : null}
      </label>
      {children}
      {error ? (
        <span className="hint" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/*
 * Appointment states as the brand's references colour them: booked in
 * neutral grey, confirmed and completed in green, in the clinic in blue (the
 * calendar draws those blocks blue too), and the two that went wrong in red
 * and amber. Each carries a glyph as well; colour is never the only signal.
 */
const STATUS_TONE: Record<string, string> = {
  confirmed: 'ok',
  completed: 'ok',
  scheduled: '',
  checked_in: 'info',
  in_treatment: 'info',
  cancelled: 'danger',
  no_show: 'warn',
};

export function AppointmentStatus({ status }: { status: string }) {
  const { t } = useI18n();
  const states = t.appointments.states as Record<string, string>;
  return <span className={`badge ${STATUS_TONE[status] ?? ''}`}>{states[status] ?? status}</span>;
}

/** Closes a popover when focus or a click leaves it. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

/**
 * A tab strip. Hidden tabs are omitted entirely rather than disabled, because a
 * tab the user can never open is noise — permission and entitlement decide
 * which ones exist at all.
 */
export function Tabs({
  value,
  onChange,
  tabs,
}: {
  value: string;
  onChange: (value: string) => void;
  tabs: { value: string; label: string; hidden?: boolean; badge?: number }[];
}) {
  const visible = tabs.filter((tab) => !tab.hidden);
  return (
    <div className="tabs" role="tablist">
      {visible.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={tab.value === value}
          className={`tab${tab.value === value ? ' active' : ''}`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 ? <span className="tab-badge">{tab.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * The WhatsApp draft dialog.
 *
 * Shows the recipient and the exact message BEFORE anything is opened, and says
 * plainly that opening it is not sending it. The audit trail records the same
 * distinction (spec §9, §12, §13, T12).
 */
export function WhatsAppDialog({
  draft,
  onClose,
}: {
  draft: { url: string; message: string; preview: { recipient: string; phoneDisplay: string; lines: string[] } };
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title={t.whatsapp.title}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              void navigator.clipboard?.writeText(draft.message).then(() => setCopied(true));
            }}
          >
            {copied ? t.whatsapp.copied : t.whatsapp.copy}
          </button>
          <a className="btn" href={draft.url} target="_blank" rel="noreferrer noopener" onClick={onClose}>
            {t.whatsapp.open}
          </a>
        </>
      }
    >
      <div className="stack">
        <div>
          <div className="section-label">{t.whatsapp.recipient}</div>
          <div style={{ marginTop: 4 }}>
            <strong>{draft.preview.recipient}</strong>{' '}
            <span className="ltr-value" style={{ color: 'var(--ink-3)' }}>
              {draft.preview.phoneDisplay}
            </span>
          </div>
        </div>

        <div className="whatsapp-preview">
          {draft.preview.lines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>

        <div className="notice">
          <span>{t.whatsapp.notSent}</span>
        </div>
      </div>
    </Modal>
  );
}
